import React from 'react';
import { View, StyleSheet } from 'react-native';
import { LineChart, XAxis, YAxis } from 'react-native-svg-charts';
import { Circle, G, Line } from 'react-native-svg';
import * as shape from 'd3-shape';
import Theme from '../../theme';
import { defaultScale, graphScale, graphScaleLine } from '../../utils/scaling';
import { calculateYAxesTickIndices, shouldRenderYAxisTickIndex } from '../utils';

const circleRadius = graphScaleLine(5);

const Circles = ({ x, y, data, showCirclesForAllPoints}) => {
  if (data[0] && showCirclesForAllPoints) {
    return data[0].data.map((value, index) => {
      return (
        <Circle
          key={index}
          cx={x(index)}
          cy={y(value)}
          r={circleRadius}
          fill={Theme.PRIMARY}
        />
      );
    });
  }

  return null;
};

const CustomGrid = ({ x, y, data, ticks, customYAxesTickIndices, useDefaultTickCalculation }) => {
  return (
    <G>
      {data[0].data.map((_, index) => {
        return (
          <Line
            key={index}
            y1={'0%'}
            y2={'100%'}
            x1={x(index)}
            x2={x(index)}
            stroke={'rgba(0,0,0,0.1)'}
          />
        );
      })}
      {ticks.map((tick, index) => {
        if ( useDefaultTickCalculation || shouldRenderYAxisTickIndex(customYAxesTickIndices, index) ) {
          return (
            <Line
              key={tick}
              y1={y(tick)}
              y2={y(tick)}
              x1={defaultScale(25)}
              x2={defaultScale(15)}
              stroke={'rgba(0,0,0,0.1)'}
            />
          );
        }
      })}
    </G>
  );
};
export const TrackingLineGraph = ({
  graphData = [],
  targetData = null,
  labels,
  height = graphScale(120),
  yAxisData = [],
  numberOfTicks = 20,
  verticalContentInset = {
    top: defaultScale(10),
    bottom: defaultScale(10),
    left: defaultScale(20),
    right: defaultScale(20) },
  horizontalContentInset = { left: defaultScale(20), right: defaultScale(20) },
  graphSvg = { stroke: Theme.PRIMARY, strokeWidth: graphScaleLine(4) },
  targetSvg = { stroke: Theme.GREY_FIGMA_LIGHT, strokeWidth: graphScaleLine(4) },
  xAxisSvg = {
    fontSize: Theme.graphStyle.regular10.fontSize,
    fill: Theme.BLACK,
    fontFamily: Theme.graphStyle.regular10.fontFamily
  },
  xAxisHeight = defaultScale(20),
  curve = shape.curveStepBefore,
  useDefaultTickCalculation = false,
  showCirclesForAllPoints = false,
}) => {
  const {customYAxesTickIndices, yMax, yMin, customTicks} = calculateYAxesTickIndices(yAxisData);
  if (graphData.length === 0) {
    return null;
  }
  let dataSet;
  if (targetData) {
    dataSet = [
      {
        data: targetData,
        svg: targetSvg,
      },
      {
        data: graphData,
        svg: graphSvg,
      },
    ];
  } else {
    dataSet = [
      {
        data: graphData,
        svg: graphSvg,
      },
    ];
  }

  return (
    <View style={[styles.graphContainer, { height }]}>
      {yAxisData && (
        <YAxis
          style={[styles.yAxis, {
            marginBottom: defaultScale(xAxisHeight)
          }]}
          data={customTicks}
          svg={{
            fontSize: Theme.graphStyle.regular10.fontSize,
            fontFamily: Theme.graphStyle.regular10.fontFamily,
            fill: Theme.BLACK,
          }}
          numberOfTicks={numberOfTicks}
          formatLabel= {(value, index) => {
            if ( useDefaultTickCalculation || shouldRenderYAxisTickIndex(customYAxesTickIndices, index)) {
              return value;
            } else {
              return '';
            }
          }}
          contentInset={verticalContentInset}
        />
      )}
      <View style={{ flex: 1 }}>
        <LineChart
          style={{ flex: 1 }}
          contentInset={verticalContentInset}
          data={dataSet}
          curve={curve}
          yMin={yMin}
          yMax={yMax}
          numberOfTicks={numberOfTicks}
          svg={graphSvg}
        >
          <CustomGrid
            customYAxesTickIndices={customYAxesTickIndices}
            useDefaultTickCalculation={useDefaultTickCalculation}
          />
          <Circles showCirclesForAllPoints={showCirclesForAllPoints}/>
        </LineChart>
        <XAxis
          data={labels}
          formatLabel={(index) => {
            return labels[index];
          }}
          svg={xAxisSvg}
          contentInset={horizontalContentInset}
          style={[{
            height: defaultScale(xAxisHeight)
          }, styles.xAxis]}
        ></XAxis>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  graphContainer: {
    flexDirection: 'row',
    backgroundColor: 'white',
  },
  yAxis: {
    width: defaultScale(25),
  },
  xAxis: {
    paddingTop: defaultScale(5),
  },
});
